import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, Plus, Pencil, Trash2, Search } from 'lucide-react';
import DateInput from '@/components/ui/date-input';
import { formatDate, formatDateTime } from '@/lib/utils';

const inputCls = 'w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white';

export default function AktuellesTab() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [onlyOffline, setOnlyOffline] = useState(false);
  const [onlyOnline, setOnlyOnline] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/app-verwaltung/news', { params: { search: search || undefined } });
      setItems(r.data || []);
    } catch (e) { toast.error('Aktuelles konnte nicht geladen werden'); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const visible = items.filter((n) =>
    (!onlyOffline || n.status === 'offline') && (!onlyOnline || n.status === 'online'));

  const startNew = () => {
    setSel(null);
    setForm({ title: '', body: '', audience: 'public', push_enabled: true, visible_from: '', visible_until: '' });
    setEditing(true);
  };
  const startEdit = () => {
    setForm({ title: sel.title, body: sel.body, audience: sel.audience,
              push_enabled: sel.push_enabled, visible_from: sel.visible_from || '', visible_until: sel.visible_until || '' });
    setEditing(true);
  };

  const save = async () => {
    if (!form.title?.trim()) { toast.error('Titel fehlt'); return; }
    const body = { ...form, visible_from: form.visible_from || null, visible_until: form.visible_until || null };
    try {
      const r = sel
        ? await api.put(`/admin/app-verwaltung/news/${sel.id}`, body)
        : await api.post('/admin/app-verwaltung/news', body);
      toast.success('Gespeichert');
      setEditing(false);
      setSel(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };

  const toggle = async () => {
    try {
      const r = await api.post(`/admin/app-verwaltung/news/${sel.id}/${sel.status === 'online' ? 'unpublish' : 'publish'}`);
      setSel(r.data);
      toast.success(r.data.status === 'online' ? 'Freigegeben (Online)' : 'Offline genommen');
      load();
    } catch (e) { toast.error('Aktion fehlgeschlagen'); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/app-verwaltung/news/${sel.id}`);
      setSel(null);
      toast.success('Gelöscht');
      load();
    } catch (e) { toast.error('Löschen fehlgeschlagen'); }
  };

  const fmt = (d) => (d ? formatDateTime(d) || '—' : '—');
  const fmtD = (d) => (d ? formatDate(d) : null);

  return (
    <div className="h-full flex">
      {/* Liste links */}
      <div className="w-96 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-3 space-y-2 border-b border-slate-100">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={onlyOffline} onChange={(e) => { setOnlyOffline(e.target.checked); setOnlyOnline(false); }}
                   data-testid="aktuelles-filter-offline" /> Nur freizugebende anzeigen
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={onlyOnline} onChange={(e) => { setOnlyOnline(e.target.checked); setOnlyOffline(false); }}
                   data-testid="aktuelles-filter-online" /> Nur freigegebene anzeigen
          </label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Schnellsuche" value={search}
                   onChange={(e) => setSearch(e.target.value)} data-testid="aktuelles-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {visible.map((n) => (
            <button key={n.id} onClick={() => { setSel(n); setEditing(false); }}
                    data-testid={`aktuelles-item-${n.id}`}
                    className={`w-full text-left flex items-stretch border-b border-slate-100 ${sel?.id === n.id ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`}>
              <span className="w-1.5 shrink-0" style={{ background: n.status === 'online' ? '#15803d' : '#ef4444' }} />
              <span className="flex-1 min-w-0 px-3 py-2.5">
                <span className="flex justify-between text-xs text-slate-400">
                  <span>{n.author_name || '—'}</span><span>{fmt(n.created_at)}</span>
                </span>
                <span className="block text-sm font-medium truncate">{n.title}</span>
                <span className="block text-xs text-slate-400 truncate">{n.body}</span>
              </span>
            </button>
          ))}
          {!visible.length && <p className="p-6 text-center text-sm text-slate-400">Keine Einträge</p>}
        </div>
        <p className="p-2 text-[11px] text-slate-400 border-t border-slate-100">Gefunden: {visible.length} / Gesamt: {items.length}</p>
      </div>

      {/* Detail Mitte */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex justify-end mb-4 gap-2">
          {sel && !editing && (
            <Button variant="outline" onClick={startEdit} data-testid="aktuelles-edit-btn">
              <Pencil className="w-4 h-4 mr-1" /> Bearbeiten
            </Button>
          )}
          <Button onClick={startNew} className="bg-brand-red hover:bg-brand-red/90" data-testid="aktuelles-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Neu
          </Button>
        </div>

        {editing ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3 max-w-2xl">
            <input className={inputCls} placeholder="Titel" value={form.title}
                   onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="aktuelles-form-title" />
            <textarea className={inputCls} rows={10} placeholder="Text…" value={form.body}
                      onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} data-testid="aktuelles-form-body" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Sichtbar ab</label>
                <DateInput  className={inputCls} value={form.visible_from}
                       onChange={(e) => setForm((p) => ({ ...p, visible_from: e.target.value }))} data-testid="aktuelles-form-from" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Sichtbar bis</label>
                <DateInput  className={inputCls} value={form.visible_until}
                       onChange={(e) => setForm((p) => ({ ...p, visible_until: e.target.value }))} data-testid="aktuelles-form-until" />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.push_enabled}
                       onChange={(e) => setForm((p) => ({ ...p, push_enabled: e.target.checked }))} data-testid="aktuelles-form-push" />
                Push-Nachricht bei Freigabe
              </label>
              <select className="border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white" value={form.audience}
                      onChange={(e) => setForm((p) => ({ ...p, audience: e.target.value }))} data-testid="aktuelles-form-audience">
                <option value="public">Öffentlich (alle App-Nutzer)</option>
                <option value="personal">Nur Personal</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={save} className="bg-brand-red hover:bg-brand-red/90" data-testid="aktuelles-form-save">Speichern</Button>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="aktuelles-form-cancel">Abbrechen</Button>
            </div>
          </div>
        ) : sel ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-2xl" data-testid="aktuelles-detail">
            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">
              {sel.audience === 'personal' ? 'Nur Personal' : 'Öffentlich'}
            </p>
            <h2 className="text-lg font-bold mb-4">{sel.title}</h2>
            <p className="text-sm whitespace-pre-wrap text-slate-700">{sel.body}</p>
          </div>
        ) : (
          <p className="text-slate-400 text-sm mt-20 text-center">Eintrag links auswählen oder „Neu“ klicken</p>
        )}
      </div>

      {/* Status-Panel rechts */}
      {sel && !editing && (
        <div className="w-64 shrink-0 border-l border-slate-200 bg-white p-4 space-y-4">
          <Button onClick={toggle} className="w-full bg-brand-red hover:bg-brand-red/90" data-testid="aktuelles-toggle-btn">
            {sel.status === 'online' ? <><EyeOff className="w-4 h-4 mr-1" /> Offline nehmen</> : <><Eye className="w-4 h-4 mr-1" /> Freigeben</>}
          </Button>
          <div className="text-sm space-y-3">
            <div><p className="text-xs text-slate-400">Status</p>
              <p className={sel.status === 'online' ? 'text-brand-success font-semibold' : 'text-rose-600 font-semibold'}>
                {sel.status === 'online' ? 'Online' : 'Offline'}</p></div>
            <div><p className="text-xs text-slate-400">Push-Nachricht</p><p>{sel.push_enabled ? 'Ja' : 'Nein'}{sel.push_sent_at ? ` (gesendet ${fmt(sel.push_sent_at)})` : ''}</p></div>
            <div><p className="text-xs text-slate-400">Zeitraum</p>
              <p>{fmtD(sel.visible_from) ? `Ab ${fmtD(sel.visible_from)}` : 'Sofort'}{fmtD(sel.visible_until) ? ` bis ${fmtD(sel.visible_until)}` : ''}</p></div>
            <div><p className="text-xs text-slate-400">Erstellung</p><p>{fmt(sel.created_at)}</p></div>
            <div><p className="text-xs text-slate-400">Änderung</p><p>{fmt(sel.updated_at)}</p></div>
          </div>
          <button onClick={remove} className="text-rose-500 text-sm flex items-center gap-1" data-testid="aktuelles-delete-btn">
            <Trash2 className="w-4 h-4" /> Löschen
          </button>
        </div>
      )}
    </div>
  );
}
